import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Platform, Pressable, View } from 'react-native';

import { DropboxPicker, type PickedDropboxFile } from '@/components/composer/dropbox-picker';
import { useTheme } from '@/components/theme-provider';
import { ActivityIndicator } from '@/components/ui/activity-indicator';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import {
	dataService,
	type ComposeTarget,
	type ComposerAttachable,
	type ReplyAttachableFile,
	type ReplyAttachmentRef,
	type StagedReplyAttachment,
} from '@/services/data-service';
import { formatDocumentSize } from '@/utils/document-download';

/**
 * Files on an outgoing email — ONE section for every composer (#603, #606): Reply, Forward, the
 * Needs-info letter and the Board Approval letter.
 *
 * Sources, each offered only where the preview says it applies: upload from the computer,
 * files already on the conversation or request, a linked request's packet PDF (conversations),
 * and the Dropbox picker.
 *
 * New bytes are STAGED the moment they are chosen, so "Open" shows exactly the file Send will
 * attach and the send itself only passes references. Nothing is attached by storage key from
 * the client — the server re-checks every reference against what this sender may attach.
 */

export interface ComposerAttachmentItem {
	/** Stable list key: the staged token or the attachment id. */
	key: string;
	ref: ReplyAttachmentRef;
	fileName: string;
	sizeBytes: number | null;
	/** Kept so Open hands iOS the right type — it decides which apps the share sheet offers. */
	mimeType: string;
	/** Set for a file already on the conversation/request, so it can be opened in place. */
	existing?: ReplyAttachableFile;
}

/** For a preview that offers nothing (an older server, or no viewer): only Upload is shown. */
export const NO_ATTACHABLE: ComposerAttachable = {
	files: [], applications: [], maxBytes: 0, dropbox: { connected: false, startPath: null },
};

export const totalAttachmentBytes = (items: ComposerAttachmentItem[]): number =>
	items.reduce((sum, item) => sum + (item.sizeBytes ?? 0), 0);

const fromStaged = (staged: StagedReplyAttachment): ComposerAttachmentItem => ({
	key: staged.token,
	ref: { staged: staged.token },
	fileName: staged.fileName,
	sizeBytes: staged.sizeBytes,
	mimeType: staged.mimeType,
});

const fromExisting = (file: ReplyAttachableFile): ComposerAttachmentItem => ({
	key: file.id,
	ref: { existing: file.id },
	fileName: file.fileName,
	sizeBytes: file.fileSize,
	mimeType: file.mimeType ?? 'application/octet-stream',
	existing: file,
});

const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

interface ComposerAttachmentsProps {
	target: ComposeTarget;
	attachable: ComposerAttachable;
	items: ComposerAttachmentItem[];
	onChange: (update: (items: ComposerAttachmentItem[]) => ComposerAttachmentItem[]) => void;
	onError: (error: unknown, fallback: string) => void;
	/**
	 * Told whenever a file starts or finishes being prepared. The composer MUST hold Send while
	 * this is true: sending mid-upload would go out without the file the UI says is on its way.
	 */
	onBusyChange?: (busy: boolean) => void;
	/** Bytes the email already carries (the board packet and its documents), in the same budget. */
	baseBytes?: number;
	/** The section heading — Forward needs its own, beside the message's linked files. */
	title?: string;
	/** What to call the existing-files source — "this conversation" or "this request". */
	existingLabel?: string;
	disabled?: boolean;
}

export const ComposerAttachments: React.FC<ComposerAttachmentsProps> = ({
	target, attachable, items, onChange, onError, onBusyChange, baseBytes = 0, title = 'Attachments', existingLabel, disabled,
}) => {
	const theme = useTheme();
	/** What is in flight, worded for the person waiting on it. */
	const [busy, setBusyLabel] = useState<string | null>(null);
	const setBusy = (label: string | null) => {
		setBusyLabel(label);
		onBusyChange?.(label !== null);
	};
	const [showExisting, setShowExisting] = useState(false);
	const [showDropbox, setShowDropbox] = useState(false);

	const total = baseBytes + totalAttachmentBytes(items);
	// Only this section's own files count as "its" problem: when the base alone is over, the
	// section that owns the base (the board letter's documents) already says so.
	const over = items.length > 0 && total > attachable.maxBytes;
	const attachedKeys = new Set(items.map((item) => item.key));
	const label = existingLabel ?? (target.kind === 'message' ? 'this conversation' : 'this request');

	const uploadFromComputer = async () => {
		// Imported here, not at the top: its native module crashes off-device, and the
		// specs that render these composers run in jsdom.
		const DocumentPicker = await import('expo-document-picker');
		const picked = await DocumentPicker.getDocumentAsync({ multiple: true, copyToCacheDirectory: true });
		if (picked.canceled || !picked.assets?.length) return;
		setBusy(picked.assets.length === 1 ? `Uploading ${picked.assets[0].name}…` : `Uploading ${picked.assets.length} files…`);
		try {
			for (const asset of picked.assets) {
				// On web the picker hands over the real File; on native it is a cached URI, sent as is.
				const file = Platform.OS === 'web'
					? (asset as { file?: File }).file
						?? new File([await (await fetch(asset.uri)).blob()], asset.name, { type: asset.mimeType ?? '' })
					: { uri: asset.uri, name: asset.name, type: asset.mimeType ?? 'application/octet-stream' };
				const staged = await dataService.stageComposeUpload(target, file);
				onChange((current) => [...current, fromStaged(staged)]);
			}
		} catch (e) {
			onError(e, 'Could not attach that file.');
		} finally {
			setBusy(null);
		}
	};

	const attachApplicationPdf = async (applicationId: string, name: string) => {
		if (target.kind !== 'message') return;
		setBusy(`Rendering ${name}…`);
		try {
			const staged = await dataService.stageReplyApplicationPdf(target.id, applicationId);
			onChange((current) => [...current, fromStaged(staged)]);
		} catch (e) {
			onError(e, 'Could not render the application PDF.');
		} finally {
			setBusy(null);
		}
	};

	/**
	 * Copy the picked Dropbox files into staging, one at a time. A file that fails is reported on
	 * its own and the rest still attach — one locked file must not cost the PM the other four.
	 */
	const attachFromDropbox = async (files: PickedDropboxFile[]) => {
		setShowDropbox(false);
		const failures: string[] = [];
		for (const [index, file] of files.entries()) {
			setBusy(files.length === 1 ? `Copying ${file.name} from Dropbox…` : `Copying ${index + 1} of ${files.length} from Dropbox…`);
			try {
				const staged = await dataService.stageComposeDropbox(target, file.id);
				onChange((current) => [...current, fromStaged(staged)]);
			} catch (e) {
				failures.push(`${file.name}: ${errorText(e)}`);
			}
		}
		setBusy(null);
		if (failures.length) onError(new Error(failures.join('\n')), 'Some Dropbox files could not be attached.');
	};

	const toggleExisting = (file: ReplyAttachableFile) => {
		onChange((current) => (current.some((item) => item.key === file.id)
			? current.filter((item) => item.key !== file.id)
			: [...current, fromExisting(file)]));
	};

	const open = (item: ComposerAttachmentItem) => {
		// Not awaited: opening must start inside the click so the browser allows the new tab.
		// Both kinds go through `openFetchedFile`, the opener that also works on iOS.
		const opening = item.existing
			? dataService.openAttachmentFile(item.existing.id, item.fileName, item.mimeType)
			: dataService.openStagedComposeFile(target, item.key, item.fileName, item.mimeType);
		opening.catch((e) => onError(e, 'Could not open that file.'));
	};

	const muted = theme.colors.onSurfaceVariant;

	return (
		<View testID="composer-attachments" style={{ marginTop: 12 }}>
			<View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
				<Text style={{ flex: 1, fontSize: 12, fontWeight: '600' }}>{title}</Text>
				{items.length ? (
					<Text testID="composer-attachments-total" style={{ fontSize: 11, color: over ? theme.colors.error : muted }}>
						{formatDocumentSize(total)} of {formatDocumentSize(attachable.maxBytes)}
					</Text>
				) : null}
			</View>

			{items.map((item) => (
				<View
					key={item.key}
					testID={`composer-attachment-${item.key}`}
					style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, gap: 8 }}
				>
					<Ionicons name="document-attach-outline" size={16} color={muted} />
					<Text style={{ flex: 1, minWidth: 0 }} numberOfLines={1}>{item.fileName}</Text>
					{item.sizeBytes != null ? (
						<Text style={{ fontSize: 11, color: muted }}>{formatDocumentSize(item.sizeBytes)}</Text>
					) : null}
					<Pressable onPress={() => open(item)} testID={`btn-open-composer-attachment-${item.key}`}>
						<Text style={{ fontSize: 12, color: theme.colors.primary }}>Open</Text>
					</Pressable>
					<Pressable
						onPress={() => onChange((current) => current.filter((i) => i.key !== item.key))}
						disabled={disabled}
						aria-label={`Remove ${item.fileName}`}
						testID={`btn-remove-composer-attachment-${item.key}`}
					>
						<Ionicons name="close" size={16} color={muted} />
					</Pressable>
				</View>
			))}

			{over ? (
				<Text testID="composer-attachments-over" style={{ fontSize: 12, color: theme.colors.error, marginTop: 4 }}>
					Over the {formatDocumentSize(attachable.maxBytes)} email limit — remove something to send.
				</Text>
			) : null}

			{busy ? (
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
					<ActivityIndicator size="small" />
					<Text style={{ fontSize: 12, color: muted }}>{busy}</Text>
				</View>
			) : (
				<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
					<Button mode="outlined" compact onPress={uploadFromComputer} disabled={disabled} testID="btn-attach-upload">
						Upload file
					</Button>
					{attachable.dropbox.connected ? (
						<Button
							mode="outlined"
							compact
							onPress={() => setShowDropbox((v) => !v)}
							disabled={disabled}
							testID="btn-attach-dropbox"
						>
							Dropbox…
						</Button>
					) : null}
					{attachable.files.length ? (
						<Button
							mode="outlined"
							compact
							onPress={() => setShowExisting((v) => !v)}
							disabled={disabled}
							testID="btn-attach-existing"
						>
							{`From ${label} (${attachable.files.length})`}
						</Button>
					) : null}
					{attachable.applications.map((application) => (
						<Button
							key={application.id}
							mode="outlined"
							compact
							onPress={() => attachApplicationPdf(application.id, application.label)}
							disabled={disabled}
							testID={`btn-attach-application-${application.id}`}
						>
							{`${application.label} (PDF)`}
						</Button>
					))}
				</View>
			)}

			{showDropbox && !busy ? (
				<DropboxPicker
					startPath={attachable.dropbox.startPath}
					onAttach={(files) => { void attachFromDropbox(files); }}
					onCancel={() => setShowDropbox(false)}
				/>
			) : null}

			{showExisting ? (
				<View testID="composer-existing-files" style={{ marginTop: 6, paddingLeft: 4 }}>
					{attachable.files.map((file) => {
						const checked = attachedKeys.has(file.id);
						return (
							<Pressable
								key={file.id}
								onPress={() => toggleExisting(file)}
								disabled={disabled}
								// `role`/`aria-*`: RN-Web 0.21 drops `accessibilityState`.
								role="checkbox"
								aria-checked={checked}
								testID={`composer-existing-file-${file.id}`}
								style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}
							>
								<Ionicons name={checked ? 'checkbox' : 'square-outline'} size={16} color={checked ? theme.colors.primary : muted} />
								<Text style={{ flex: 1, minWidth: 0 }} numberOfLines={1}>{file.fileName}</Text>
								<Text style={{ fontSize: 11, color: muted }}>
									{file.origin === 'request' ? 'on the request' : 'on this conversation'}
									{file.fileSize != null ? ` · ${formatDocumentSize(file.fileSize)}` : ''}
								</Text>
							</Pressable>
						);
					})}
				</View>
			) : null}
		</View>
	);
};
